(() => {
  if (customElements.get('kd-collection-v2')) return;
  const isColor = name => /^(colou?r|kleur|farbe)$/i.test(name);
  const isSize = name => /^(size|maat|größe|grösse)$/i.test(name);
  const matching = (variants, options) => variants.find(v => v.options.every((value, i) => value === options[i]));
  const node = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text != null) el.textContent = text;
    return el;
  };
  customElements.define('kd-collection-v2', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const listen = (el, event, fn) => el.addEventListener(event, fn, {signal:this.controller.signal});
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-quick-form]');
      this.error = this.querySelector('[data-error]');
      this.submit = this.querySelector('[data-submit]');
      this.cards = new Map();
      this.querySelectorAll('[data-card]').forEach(card => {
        const data = JSON.parse(card.querySelector('[data-product]').textContent);
        data.colorIndex = data.options.findIndex(o => isColor(o.name));
        this.cards.set(card, data);
        this.renderSwatches(card, data);
      });
      listen(this, 'click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-close')) { this.dialog.close(); return; }
        const card = button.closest('[data-card]');
        if (card && button.hasAttribute('data-quick-add')) this.open(card, button);
        if (card && button.dataset.colorValue != null) this.selectCardColor(card, button.dataset.colorValue);
        if (button.dataset.optionIndex != null) this.selectOption(Number(button.dataset.optionIndex), button.dataset.value);
      });
      listen(this.dialog, 'click', e => {
        if (e.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) this.dialog.close();
      });
      listen(this.dialog, 'close', () => {
        document.documentElement.style.overflow = this.previousOverflow ?? '';
        if (!document.querySelector('kd-cart-drawer.kd-cart--active')) this.opener?.focus();
      });
      listen(this.form, 'submit', e => { e.preventDefault(); this.addToCart(); });
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      this.controller = null;
    }
    variantForColor(data, value) {
      return data.variants.find(v => v.options[data.colorIndex] === value && v.available) || data.variants.find(v => v.options[data.colorIndex] === value);
    }
    renderSwatches(card, data) {
      const container = card.querySelector('[data-swatches]');
      container.replaceChildren();
      if (data.colorIndex < 0) return;
      const selected = data.variants.find(v => v.id === data.selected);
      const values = data.options[data.colorIndex].values;
      values.slice(0, 4).forEach(value => {
        const variant = this.variantForColor(data, value);
        const button = node('button', 'kd-cv2__swatch');
        button.type = 'button'; button.dataset.colorValue = value;
        button.setAttribute('aria-label', value); button.title = value;
        button.setAttribute('aria-pressed', String(selected?.options[data.colorIndex] === value));
        if (variant?.image) {
          const img = node('img'); img.src = variant.image; img.alt = ''; img.loading = 'lazy'; button.append(img);
        } else button.textContent = value.slice(0, 2);
        container.append(button);
      });
      if (values.length > 4) {
        const more = node('button', 'kd-cv2__more', `+${values.length - 4}`);
        more.type = 'button'; more.setAttribute('data-quick-add', '');
        more.setAttribute('aria-label', `${this.labels.color}: +${values.length - 4}`);
        more.setAttribute('aria-haspopup', 'dialog'); container.append(more);
      }
    }
    selectCardColor(card, value) {
      const data = this.cards.get(card);
      const variant = this.variantForColor(data, value);
      if (!variant) return;
      data.selected = variant.id;
      card.querySelector('[data-price]').textContent = variant.price;
      card.querySelector('[data-compare]').textContent = variant.compare;
      card.querySelector('[data-compare]').hidden = !variant.sale;
      card.querySelector('[data-sale]').hidden = !variant.sale;
      card.querySelectorAll('[data-product-link]').forEach(a => {
        const url = new URL(data.url, location.origin); url.searchParams.set('variant', variant.id); a.href = url.href;
      });
      const image = card.querySelector('img.kd-cv2__image');
      if (image && variant.image) { image.removeAttribute('srcset'); image.src = variant.image; }
      this.renderSwatches(card, data);
    }
    open(card, opener) {
      if (this.busy) return;
      this.data = this.cards.get(card);
      const selected = this.data.variants.find(v => v.id === this.data.selected) || this.data.variants[0];
      if (!selected) return;
      this.options = [...selected.options];
      this.opener = opener;
      this.error.textContent = '';
      this.querySelector('[data-product-name]').textContent = this.data.title;
      this.renderOptions();
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
    }
    selectOption(index, value) {
      if (this.busy) return;
      this.options[index] = value;
      // Changing color keeps the selected size when possible; otherwise pick an available combination.
      if (isColor(this.data.options[index].name) && !matching(this.data.variants, this.options)?.available) {
        const next = this.data.variants.find(v => v.available && v.options[index] === value);
        if (next) this.options = [...next.options];
      }
      this.error.textContent = '';
      this.renderOptions();
      const selectedButton = [...this.querySelectorAll('[data-option-index]')].find(b => Number(b.dataset.optionIndex) === index && b.dataset.value === value);
      selectedButton?.focus();
    }
    renderOptions() {
      const stock = this.querySelector('[data-stock]');
      let stockRow = null;
      const root = this.querySelector('[data-options]'); root.replaceChildren();
      if (!this.data.defaultOnly) this.data.options.forEach((option, index) => {
        const field = node('fieldset', 'kd-cv2__option');
        const label = isColor(option.name) ? this.labels.color : isSize(option.name) ? this.labels.size : option.name;
        const legend = node('legend');
        const selection = node('span');
        selection.append(node('strong', '', `${label}: `), document.createTextNode(this.options[index]));
        if (isSize(option.name)) {
          legend.className = 'kd-cv2__size-legend';
          stockRow = node('span', 'kd-cv2__size-row');
          stockRow.append(selection, stock);
          legend.append(stockRow);
        } else {
          legend.append(selection);
        }
        field.append(legend);
        const choices = node('div', 'kd-cv2__choices');
        const configured = this.data.groups?.[option.name] || this.data.groups?.[option.name.toLowerCase()];
        const groups = Array.isArray(configured) ? configured : [];
        const used = new Set();
        const appendChoice = value => {
          if (used.has(value) || !option.values.includes(value)) return;
          used.add(value);
          const color = isColor(option.name);
          const representative = this.data.variants.find(v => v.options[index] === value && v.image);
          const button = node('button', 'kd-cv2__choice' + (color && representative?.image ? ' kd-cv2__choice--image' : ''));
          button.type = 'button'; button.dataset.optionIndex = index; button.dataset.value = value;
          button.setAttribute('aria-label', value); button.title = value;
          button.setAttribute('aria-pressed', String(this.options[index] === value));
          button.disabled = !this.data.variants.some(v => v.available && v.options[index] === value && (color || v.options.every((val, i) => i === index || val === this.options[i])));
          if (color && representative?.image) {
            const image = node('img'); image.src = representative.image; image.alt = ''; button.append(image);
          } else button.textContent = value;
          choices.append(button);
        };
        groups.forEach(group => {
          const values = Array.isArray(group.values) ? group.values : [];
          if (!values.some(v => option.values.includes(v))) return;
          let label = String(group.label || '');
          if (/^(effen|solid|solids)$/i.test(label)) label = this.labels.solid;
          if (/^(combinatie|combinaties|combination|combinations)$/i.test(label)) label = this.labels.combination;
          choices.append(node('p', 'kd-cv2__group-title', label)); values.forEach(appendChoice);
        });
        option.values.forEach(appendChoice); field.append(choices); root.append(field);
      });
      this.variant = matching(this.data.variants, this.options);
      const available = this.variant?.available;
      if (!stockRow) root.append(stock);
      stock.textContent = available ? `● ${this.labels.inStock}` : this.variant ? this.labels.soldOut : this.labels.unavailable;
      stock.classList.toggle('is-unavailable', !available);
      this.submit.disabled = !available;
      this.submit.textContent = available ? this.labels.add : this.variant ? this.labels.soldOut : this.labels.unavailable;
    }
    async addToCart() {
      if (this.busy || !this.variant?.available) return;
      this.busy = true; this.submit.disabled = true; this.submit.textContent = this.labels.adding;
      this.form.setAttribute('aria-busy', 'true'); this.error.textContent = '';
      try {
        const response = await fetch(this.dataset.cartUrl + '.js', {
          method:'POST', headers:{'Content-Type':'application/json', 'Accept':'application/json'},
          body:JSON.stringify({items:[{id:this.variant.id,quantity:1}]})
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.labels.error);
        this.dialog.close();
        const drawer = document.querySelector('kd-cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          if (!drawer.classList.contains('kd-cart--active')) drawer.open();
        } else location.assign(this.dataset.cartPage);
      } catch (error) {
        this.error.textContent = error.message || this.labels.error;
      } finally {
        this.busy = false; this.form.removeAttribute('aria-busy');
        this.submit.disabled = !this.variant?.available;
        this.submit.textContent = this.variant?.available ? this.labels.add : this.labels.soldOut;
      }
    }
  });
})();
